/**
 * ResumeTab — Two-column summary view for an affaire (inline editable)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Affaire } from '@/features/admin/tabs/search/types/search.types';
import type { AffaireContact } from '@/features/admin/tabs/tracker/types/tracker.types';
import type { BienData, AffaireDetailTab } from '../types/affaire-detail.types';
import { fmtCurrency, fmtDate, getDaysSince } from '../utils/formatters';
import { useAuth } from '@/core/auth';
import { apiClient } from '@/core/api/client';
import { CustomerDrawer } from './CustomerDrawer';

interface ResumeTabProps {
  affaire: Affaire;
  contacts: AffaireContact[];
  biens: BienData[];
  contactsLoading: boolean;
  biensLoading: boolean;
  onRefresh?: () => void;
  onTabSwitch?: (tab: AffaireDetailTab) => void;
}

export const ResumeTab: React.FC<ResumeTabProps> = ({
  affaire, contacts, biens, contactsLoading, biensLoading, onRefresh, onTabSwitch,
}) => {
  const { isAdmin } = useAuth();
  const [drawerContact, setDrawerContact] = useState<{ fmContactId: number | null; name: string } | null>(null);
  const bien = biens[0];
  const a = affaire as any;
  const isNewDossier = parseInt(affaire.id_unique) >= 90000;

  // ── Honoraires: sum from billing products ──
  const { data: productsData } = useQuery({
    queryKey: ['affaire-products', affaire.id_unique],
    queryFn: async () => {
      try {
        const res: any = await apiClient.get(`/api/billing/affaires/${affaire.id_unique}/products`);
        return res.success ? res.data : [];
      } catch { return []; }
    },
    staleTime: 60_000,
  });
  const honorairesSum = (productsData || []).reduce((sum: number, p: any) => sum + (Number(p.honoraire_expert_htva) || 0), 0);

  // ── Contact add form ──
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<any[]>([]);
  const [newContactMode, setNewContactMode] = useState(false);
  const [contactType, setContactType] = useState<'person' | 'company'>('person');
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', company_name: '', vat_number: '', email: '', phone: '', address_street: '', address_postcode: '', address_city: '' });
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['demandeur']);
  const ROLES = ['demandeur', 'intermédiaire', 'propriétaire', 'apporteur', 'notaire', 'banque', 'expert', 'locataire'];
  const [customRoleMode, setCustomRoleMode] = useState(false);
  const [customRoleText, setCustomRoleText] = useState('');
  const [importDossierId, setImportDossierId] = useState('');
  const [importContacts, setImportContacts] = useState<any[]>([]);
  const [importSelected, setImportSelected] = useState<Set<number>>(new Set());

  const toggleRole = (role: string) => {
    setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const searchCustomers = useCallback(async (q: string) => {
    if (q.length < 2) { setContactResults([]); return; }
    try {
      const res: any = await apiClient.get(`/api/customers/search?q=${encodeURIComponent(q)}`);
      setContactResults(res.success ? (res.data || []).slice(0, 5) : []);
    } catch { setContactResults([]); }
  }, []);

  const addExistingContact = useCallback(async (customerId: string) => {
    try {
      for (const role of selectedRoles) {
        try { await apiClient.post(`/api/dossiers/${affaire.id_unique}/contacts`, { customer_id: customerId, role }); } catch { /* skip duplicates */ }
      }
      setShowAddContact(false); setContactSearch(''); setContactResults([]);
      if (onRefresh) onRefresh();
    } catch (e: any) { alert(e?.response?.data?.message || 'Erreur'); }
  }, [affaire.id_unique, selectedRoles, onRefresh]);

  const createAndAddContact = useCallback(async () => {
    try {
      const payload = contactType === 'person'
        ? { type: 'person', first_name: newContact.first_name, last_name: newContact.last_name, email: newContact.email, phone: newContact.phone, address_street: newContact.address_street, address_postcode: newContact.address_postcode, address_city: newContact.address_city }
        : { type: 'company', company_name: newContact.company_name, vat_number: newContact.vat_number, email: newContact.email, phone: newContact.phone, address_street: newContact.address_street, address_postcode: newContact.address_postcode, address_city: newContact.address_city };
      const res: any = await apiClient.post('/api/customers', payload);
      if (res.success && res.data?.id) {
        for (const role of selectedRoles) {
          try { await apiClient.post(`/api/dossiers/${affaire.id_unique}/contacts`, { customer_id: res.data.id, role }); } catch { /* skip duplicates */ }
        }
        setShowAddContact(false); setNewContactMode(false);
        setNewContact({ first_name: '', last_name: '', company_name: '', vat_number: '', email: '', phone: '', address_street: '', address_postcode: '', address_city: '' });
        if (onRefresh) onRefresh();
      }
    } catch (e: any) { alert(e?.response?.data?.message || 'Erreur'); }
  }, [affaire.id_unique, selectedRoles, newContact, contactType, onRefresh]);

  const searchImportDossier = useCallback(async () => {
    if (!importDossierId) return;
    try {
      const id = parseInt(importDossierId);
      if (id >= 90000) {
        const res: any = await apiClient.get(`/api/dossiers/${id}`);
        setImportContacts(res.success ? (res.data?.contacts || []) : []);
      } else {
        const res: any = await apiClient.get(`/api/fm/affaire/${id}/contacts`);
        setImportContacts(res.success ? (res.data || []) : []);
      }
    } catch { setImportContacts([]); }
  }, [importDossierId]);

  const doImportContacts = useCallback(async () => {
    for (const idx of importSelected) {
      const c = importContacts[idx];
      if (!c) continue;
      const customerId = c.customer_id || c.id;
      const role = c.role || 'demandeur';
      if (customerId) {
        try { await apiClient.post(`/api/dossiers/${affaire.id_unique}/contacts`, { customer_id: customerId, role }); } catch { /* skip */ }
      }
    }
    setImportContacts([]); setImportDossierId(''); setImportSelected(new Set());
    if (onRefresh) onRefresh();
  }, [affaire.id_unique, importContacts, importSelected, onRefresh]);

  const removeContact = useCallback(async (contactId: number | string) => {
    try {
      await apiClient.delete(`/api/dossiers/${affaire.id_unique}/contacts/${contactId}`);
      if (onRefresh) onRefresh();
    } catch (e) { console.error('Remove contact failed:', e); }
  }, [affaire.id_unique, onRefresh]);

  // ── Inline edit state ──
  const [editField, setEditField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editField && inputRef.current) inputRef.current.focus(); }, [editField]);

  const startEdit = (field: string, currentValue: any) => {
    if (!isNewDossier) return; // Only editable for new dossiers
    setEditField(field);
    setEditValue(currentValue ?? '');
  };

  const saveEdit = useCallback(async () => {
    if (!editField || !isNewDossier) return;
    setSaving(true);
    try {
      await apiClient.put(`/api/dossiers/${affaire.id_unique}`, { [editField]: editValue || null });
      // Update local affaire object
      (affaire as any)[editField] = editValue || null;
      if (onRefresh) onRefresh();
    } catch (e) { console.error('Save failed:', e); }
    setSaving(false);
    setEditField(null);
  }, [editField, editValue, affaire, isNewDossier, onRefresh]);

  const cancelEdit = () => { setEditField(null); };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  // ── Editable field component ──
  const EditableField: React.FC<{
    field: string; label: string; value: any;
    type?: 'text' | 'date' | 'number' | 'currency';
    formatter?: (v: any) => string;
  }> = ({ field, label, value, type = 'text', formatter }) => {
    const isEditing = editField === field;
    const displayValue = formatter ? formatter(value) : (value || '-');

    return (
      <div>
        <div className="aff-detail-info-label">{label}</div>
        {isEditing ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              ref={inputRef}
              type={type === 'currency' ? 'number' : type === 'date' ? 'date' : 'text'}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={saveEdit}
              className="aff-detail-inline-input"
              step={type === 'currency' ? '0.01' : undefined}
            />
          </div>
        ) : (
          <div
            className={`aff-detail-info-value ${isNewDossier ? 'aff-detail-info-editable' : ''}`}
            onClick={() => startEdit(field, type === 'date' && value ? new Date(value).toISOString().split('T')[0] : value)}
            title={isNewDossier ? 'Cliquer pour modifier' : undefined}
          >
            {displayValue}
          </div>
        )}
      </div>
    );
  };

  const formatAddress = (b: BienData): string => {
    const addr = b.address;
    if (!addr) return 'Adresse non renseignée';
    const parts = [addr.number, addr.street].filter(Boolean).join(' ');
    const city = [addr.postcode, addr.locality].filter(Boolean).join(' ');
    return [parts, city].filter(Boolean).join(', ') || 'Adresse non renseignée';
  };

  return (
    <div className="aff-detail-resume">
      {/* ===== LEFT COLUMN ===== */}
      <div className="aff-detail-resume-left">

        {/* Property Address Card */}
        <div className="aff-detail-card aff-detail-card-highlight">
          <div className="aff-detail-card-label">Adresse du bien</div>
          {biensLoading ? (
            <div className="aff-detail-skeleton" style={{ height: 24, width: '80%' }} />
          ) : (
            <>
              <div className="aff-detail-address">
                {bien ? formatAddress(bien)
                  : affaire.address ? `${affaire.address.number || ''} ${affaire.address.street || ''}, ${affaire.address.postcode || ''} ${affaire.address.locality || ''}`.trim()
                  : 'Adresse non renseignée'}
              </div>
              {bien && (
                <div className="aff-detail-tags">
                  {bien.type_bien_unifie && <span className="aff-detail-tag aff-detail-tag-primary">{bien.type_bien_unifie}</span>}
                  {bien.surfaces?.superficie_parcelle != null && <span className="aff-detail-tag">{bien.surfaces.superficie_parcelle} m² parc.</span>}
                  {bien.surfaces?.surface_habitable != null && <span className="aff-detail-tag">{bien.surfaces.surface_habitable} m² hab.</span>}
                </div>
              )}
            </>
          )}
        </div>

        {/* Info Grid */}
        <div className="aff-detail-card">
          <div className="aff-detail-card-header"><h3>Informations</h3></div>
          <div className="aff-detail-info-grid">
            <div>
              <div className="aff-detail-info-label">N° Affaire</div>
              <div className="aff-detail-info-value font-bold">{affaire.id_unique}</div>
            </div>
            <EditableField field="date_demande" label="Date demande" value={affaire.date_demande} type="date" formatter={fmtDate} />
            <EditableField field="type" label="Type mission" value={affaire.type_mission} />
            <EditableField field="montant_tvac" label="Montant TVAC" value={affaire.montant_tvac_affaire} type="currency" formatter={fmtCurrency} />
            <EditableField field="code_produit" label="Code produit" value={a.code_produit} />
            <EditableField field="ref_client" label="Réf. client" value={a.ref_client} />
            <EditableField field="ref_banque" label="Réf. banque" value={a.ref_banque} />
            <EditableField field="projet" label="Projet" value={a.projet} />
            <EditableField field="deadline" label="Deadline" value={a.deadline} type="date" formatter={fmtDate} />
            <div>
              <div className="aff-detail-info-label">Honoraires Expert</div>
              <div className="aff-detail-info-value font-bold" style={{ color: '#16A34A' }}>{fmtCurrency(honorairesSum)}</div>
              <div style={{ fontSize: 10, color: '#858892', marginTop: 2, cursor: 'pointer' }}
                onClick={() => onTabSwitch?.('facturation')}>Voir produits →</div>
            </div>
            <div>
              <div className="aff-detail-info-label">Expert</div>
              <div className="aff-detail-info-value">{affaire.expert || 'Non assigné'}</div>
            </div>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="aff-detail-card">
          <div className="aff-detail-card-header">
            <h3>Contacts</h3>
            <span className="aff-detail-count">{contacts.length}</span>
          </div>
          {contactsLoading ? (
            <div className="aff-detail-card-body">
              <div className="aff-detail-skeleton" style={{ height: 16, width: '60%', marginBottom: 8 }} />
              <div className="aff-detail-skeleton" style={{ height: 16, width: '80%' }} />
            </div>
          ) : contacts.length === 0 ? (
            <div className="aff-detail-card-empty">Aucun contact lié</div>
          ) : (
            <div className="aff-detail-table-wrap">
              <table className="aff-detail-table">
                <thead><tr><th>Rôle</th><th>Nom</th><th>Email</th><th>Tél</th>{isNewDossier && <th></th>}</tr></thead>
                <tbody>
                  {contacts.map((c, idx) => (
                    <tr key={`${c.k_id_contact}-${idx}`}>
                      <td>
                        <div className="aff-detail-role-badges">
                          {c.roles.map(r => <span key={r} className="aff-detail-role-badge">{r}</span>)}
                        </div>
                      </td>
                      <td className="font-medium">
                        {isAdmin ? (
                          <span className="aff-detail-contact-link" onClick={() => setDrawerContact({
                            fmContactId: c.k_id_contact ? parseInt(c.k_id_contact) : null,
                            name: [c.prenom, c.nom_representant].filter(Boolean).join(' ') || '-'
                          })}>{[c.prenom, c.nom_representant].filter(Boolean).join(' ') || '-'}</span>
                        ) : [c.prenom, c.nom_representant].filter(Boolean).join(' ') || '-'}
                      </td>
                      <td>{c.email_1 || '-'}</td>
                      <td>{c.tel_mobile || '-'}</td>
                      {isNewDossier && (
                        <td>
                          <button className="aff-detail-contact-remove" onClick={() => {
                            const rowId = (c as any).contact_row_id;
                            if (!rowId) { alert('ID contact manquant — rechargez la page'); return; }
                            removeContact(rowId);
                          }} title="Retirer">×</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add contact form */}
          {isNewDossier && !showAddContact && (
            <button className="aff-detail-add-contact-btn" onClick={() => setShowAddContact(true)}>
              + Ajouter un contact
            </button>
          )}
          {isNewDossier && showAddContact && (
            <div className="aff-detail-add-contact-form">
              {/* Role pills — multi-select */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#EF5343', fontWeight: 600, marginBottom: 6 }}>Rôle(s) à assigner :</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {ROLES.map(r => (
                    <button key={r} type="button" onClick={() => toggleRole(r)}
                      style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 50, cursor: 'pointer', transition: 'all 0.15s', border: '1.5px solid', ...(selectedRoles.includes(r) ? { background: '#EF5343', color: '#fff', borderColor: '#EF5343' } : { background: '#fff', color: '#64748B', borderColor: '#E5E7EB' }) }}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                  {/* Custom role */}
                  {!customRoleMode ? (
                    <button type="button" onClick={() => setCustomRoleMode(true)}
                      style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 50, cursor: 'pointer', border: '1.5px dashed #CBD5E1', background: '#fff', color: '#94A3B8' }}>
                      Autre...
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input type="text" value={customRoleText} onChange={e => setCustomRoleText(e.target.value)}
                        placeholder="Rôle..." autoFocus onKeyDown={e => {
                          if (e.key === 'Enter' && customRoleText.trim()) { toggleRole(customRoleText.trim().toLowerCase()); setCustomRoleMode(false); setCustomRoleText(''); }
                          if (e.key === 'Escape') { setCustomRoleMode(false); setCustomRoleText(''); }
                        }}
                        style={{ padding: '4px 8px', fontSize: 11, border: '1.5px solid #EF5343', borderRadius: 50, outline: 'none', width: 100 }} />
                      <button type="button" onClick={() => { if (customRoleText.trim()) { toggleRole(customRoleText.trim().toLowerCase()); } setCustomRoleMode(false); setCustomRoleText(''); }}
                        style={{ padding: '4px 8px', fontSize: 10, background: '#EF5343', color: '#fff', border: 'none', borderRadius: 50, cursor: 'pointer' }}>OK</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Search existing */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                {!newContactMode && (
                  <input type="text" placeholder="Rechercher par nom ou email..." value={contactSearch}
                    onChange={e => { setContactSearch(e.target.value); searchCustomers(e.target.value); }}
                    className="aff-detail-inline-input" style={{ flex: 1 }} />
                )}
                <button onClick={() => { setShowAddContact(false); setNewContactMode(false); setContactSearch(''); setContactResults([]); }}
                  style={{ background: 'none', border: 'none', color: '#858892', cursor: 'pointer', fontSize: 18 }}>×</button>
              </div>

              {/* Search results */}
              {!newContactMode && contactResults.length > 0 && (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                  {contactResults.map((c: any) => (
                    <div key={c.id} onClick={() => addExistingContact(c.id)}
                      style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#FFF8F8')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}>
                      <span style={{ fontWeight: 600 }}>{c.first_name} {c.last_name} {c.company_name}</span>
                      <span style={{ color: '#858892' }}>{c.email}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* New contact form */}
              {!newContactMode && (
                <button onClick={() => setNewContactMode(true)}
                  style={{ fontSize: 12, color: '#EF5343', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                  + Créer un nouveau contact
                </button>
              )}
              {newContactMode && (
                <div style={{ border: '1px solid #EDD7D5', borderRadius: 10, padding: 14, marginBottom: 10, background: '#FAFAFA' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Nouveau client</span>
                    <button onClick={() => setNewContactMode(false)} style={{ background: 'none', border: 'none', color: '#858892', cursor: 'pointer', fontSize: 16 }}>×</button>
                  </div>
                  {/* Personne / Société toggle */}
                  <div style={{ display: 'flex', gap: 0, marginBottom: 12, border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                    <button onClick={() => setContactType('person')}
                      style={{ flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', ...(contactType === 'person' ? { background: '#002035', color: '#fff' } : { background: '#fff', color: '#64748B' }) }}>
                      Personne
                    </button>
                    <button onClick={() => setContactType('company')}
                      style={{ flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', borderLeft: '1px solid #E5E7EB', ...(contactType === 'company' ? { background: '#002035', color: '#fff' } : { background: '#fff', color: '#64748B' }) }}>
                      Société
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                    {contactType === 'person' ? (
                      <>
                        <div><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>Prénom</div><input value={newContact.first_name} onChange={e => setNewContact(p => ({ ...p, first_name: e.target.value }))} className="aff-detail-inline-input" /></div>
                        <div><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>Nom *</div><input value={newContact.last_name} onChange={e => setNewContact(p => ({ ...p, last_name: e.target.value }))} className="aff-detail-inline-input" /></div>
                      </>
                    ) : (
                      <>
                        <div style={{ gridColumn: '1/-1' }}><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>Nom de société *</div><input value={newContact.company_name} onChange={e => setNewContact(p => ({ ...p, company_name: e.target.value }))} className="aff-detail-inline-input" /></div>
                        <div style={{ gridColumn: '1/-1' }}><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>N° TVA</div><input placeholder="BE0123456789" value={newContact.vat_number} onChange={e => setNewContact(p => ({ ...p, vat_number: e.target.value }))} className="aff-detail-inline-input" /></div>
                      </>
                    )}
                    <div><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>Email</div><input type="email" value={newContact.email} onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))} className="aff-detail-inline-input" /></div>
                    <div><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>Téléphone</div><input value={newContact.phone} onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))} className="aff-detail-inline-input" /></div>
                    <div><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>Rue</div><input value={newContact.address_street} onChange={e => setNewContact(p => ({ ...p, address_street: e.target.value }))} className="aff-detail-inline-input" /></div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <div style={{ flex: 1 }}><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>CP</div><input value={newContact.address_postcode} onChange={e => setNewContact(p => ({ ...p, address_postcode: e.target.value }))} className="aff-detail-inline-input" /></div>
                      <div style={{ flex: 2 }}><div style={{ fontSize: 10, color: '#858892', marginBottom: 2 }}>Ville</div><input value={newContact.address_city} onChange={e => setNewContact(p => ({ ...p, address_city: e.target.value }))} className="aff-detail-inline-input" /></div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => setNewContactMode(false)} style={{ padding: '7px 16px', background: '#fff', color: '#64748B', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Annuler</button>
                    <button onClick={createAndAddContact}
                      style={{ padding: '7px 16px', background: '#EF5343', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                      Créer le client
                    </button>
                  </div>
                </div>
              )}

              {/* Import from another dossier */}
              <div style={{ border: '1px solid #EDD7D5', borderRadius: 10, padding: 14, marginTop: 10, background: '#FAFAFA' }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Importer depuis un dossier</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" placeholder="N° dossier (ex: 71000)" value={importDossierId}
                    onChange={e => setImportDossierId(e.target.value.replace(/[^0-9]/g, ''))}
                    className="aff-detail-inline-input" style={{ flex: 1 }} />
                  <button onClick={searchImportDossier}
                    style={{ padding: '7px 16px', background: '#fff', color: '#002035', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Chercher
                  </button>
                </div>
                {importContacts.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {importContacts.map((c: any, i: number) => (
                      <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={importSelected.has(i)}
                          onChange={() => setImportSelected(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; })} />
                        <span style={{ fontWeight: 600 }}>{c.first_name} {c.last_name} {c.company_name}</span>
                        <span style={{ color: '#858892', fontSize: 11 }}>— {c.role}</span>
                      </label>
                    ))}
                    <button onClick={doImportContacts} disabled={importSelected.size === 0}
                      style={{ marginTop: 6, padding: '7px 16px', background: importSelected.size > 0 ? '#EF5343' : '#CBD5E1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: importSelected.size > 0 ? 'pointer' : 'default' }}>
                      Importer sélectionnés ({importSelected.size})
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== RIGHT COLUMN ===== */}
      <div className="aff-detail-resume-right">

        {/* Key Dates Card — Full pipeline */}
        <div className="aff-detail-card">
          <div className="aff-detail-card-header"><h3>Dates clés</h3></div>
          <div className="aff-detail-timeline-mini">
            <EditableTimelineItem label="IN" field="date_in" value={a.date_in} isNew={isNewDossier} affaireId={affaire.id_unique} affaire={affaire} onRefresh={onRefresh} />
            <EditableTimelineItem label="RV" field="date_rv" value={affaire.date_du_rv} extra={a.heure_rv} isNew={isNewDossier} affaireId={affaire.id_unique} affaire={affaire} onRefresh={onRefresh} />
            <EditableTimelineItem label="Réception docs" field="date_reception_docs" value={a.date_reception_docs} isNew={isNewDossier} affaireId={affaire.id_unique} affaire={affaire} onRefresh={onRefresh} />
            <EditableTimelineItem label="EVAL 1" field="date_eval1" value={a.date_eval1} isNew={isNewDossier} affaireId={affaire.id_unique} affaire={affaire} onRefresh={onRefresh} />
            <EditableTimelineItem label="EVAL 2" field="date_eval2" value={a.date_eval2} isNew={isNewDossier} affaireId={affaire.id_unique} affaire={affaire} onRefresh={onRefresh} />
            <EditableTimelineItem label="EVAL 3" field="date_eval3" value={a.date_eval3} isNew={isNewDossier} affaireId={affaire.id_unique} affaire={affaire} onRefresh={onRefresh} />
            <EditableTimelineItem label="OUT" field="date_out" value={a.date_out || affaire.date_sortie} isNew={isNewDossier} affaireId={affaire.id_unique} affaire={affaire} onRefresh={onRefresh} />
          </div>
        </div>

        {/* Facturation */}
        <div className="aff-detail-card">
          <div className="aff-detail-card-header"><h3>Facturation</h3></div>
          <div className="aff-detail-billing-summary">
            <div className="aff-detail-billing-row">
              <span className="aff-detail-billing-label">Honoraires</span>
              <span className="aff-detail-billing-value font-bold">{fmtCurrency(a.honoraires_expert)}</span>
            </div>
            <div className="aff-detail-billing-row">
              <span className="aff-detail-billing-label">Montant TVAC</span>
              <span className="aff-detail-billing-value">{fmtCurrency(affaire.montant_tvac_affaire)}</span>
            </div>
            <div className="aff-detail-billing-row">
              <span className="aff-detail-billing-label">Facturé</span>
              <span className="aff-detail-billing-value">
                {affaire.facturation?.affaire_facturee === 'Oui'
                  ? <span className="text-green-600">Oui</span>
                  : <span className="text-gray-400">Non</span>}
              </span>
            </div>
            <div className="aff-detail-billing-row">
              <span className="aff-detail-billing-label">Paiement</span>
              <span className="aff-detail-billing-value">
                {a.date_paiement
                  ? <span className="text-green-600">{fmtDate(a.date_paiement)}</span>
                  : <span className="text-gray-400">En attente</span>}
              </span>
            </div>
          </div>
        </div>

        {/* Surfaces */}
        {bien && (bien.surfaces?.surface_habitable != null || bien.surfaces?.superficie_parcelle != null) && (
          <div className="aff-detail-card">
            <div className="aff-detail-card-header"><h3>Surfaces</h3></div>
            <div className="aff-detail-info-grid aff-detail-info-grid-compact">
              {bien.surfaces?.surface_habitable != null && <div><div className="aff-detail-info-label">Habitable</div><div className="aff-detail-info-value">{bien.surfaces.surface_habitable} m²</div></div>}
              {bien.surfaces?.superficie_parcelle != null && <div><div className="aff-detail-info-label">Parcelle</div><div className="aff-detail-info-value">{bien.surfaces.superficie_parcelle} m²</div></div>}
              {bien.surfaces?.surface_cumul != null && <div><div className="aff-detail-info-label">Cumul</div><div className="aff-detail-info-value">{bien.surfaces.surface_cumul} m²</div></div>}
            </div>
          </div>
        )}
      </div>

      {drawerContact && (
        <CustomerDrawer fmContactId={drawerContact.fmContactId} contactName={drawerContact.name} onClose={() => setDrawerContact(null)} />
      )}
    </div>
  );
};

/* Editable timeline item — click date to edit */
const EditableTimelineItem: React.FC<{
  label: string; field: string; value?: string | null; extra?: string | null;
  isNew: boolean; affaireId: string; affaire: any; onRefresh?: () => void;
}> = ({ label, field, value, extra, isNew, affaireId, affaire, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ? new Date(value).toISOString().split('T')[0] : '');
  const days = value ? getDaysSince(value) : null;

  const save = async () => {
    try {
      await apiClient.put(`/api/dossiers/${affaireId}`, { [field]: val || null });
      affaire[field] = val || null;
      if (onRefresh) onRefresh();
    } catch (e) { console.error('Save date failed:', e); }
    setEditing(false);
  };

  return (
    <div className="aff-detail-timeline-item">
      <div className="aff-detail-timeline-dot" />
      <div className="aff-detail-timeline-content">
        <span className="aff-detail-timeline-label">{label}</span>
        {editing ? (
          <input type="date" value={val} onChange={e => setVal(e.target.value)}
            onBlur={save} onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
            autoFocus className="aff-detail-inline-input" style={{ width: 140 }} />
        ) : (
          <span className={`aff-detail-timeline-date ${isNew ? 'aff-detail-info-editable' : ''}`}
            onClick={() => isNew && setEditing(true)} title={isNew ? 'Cliquer pour modifier' : undefined}>
            {value ? (
              <>
                {fmtDate(value)}
                {extra && <span className="text-gray-400 ml-1">{extra}</span>}
                {days != null && days >= 0 && <span className="aff-detail-timeline-days">J+{days}</span>}
              </>
            ) : <span className="text-gray-300">-</span>}
          </span>
        )}
      </div>
    </div>
  );
};
